// React hooks that fetch real data from the /api proxy and fall back to the
// bundled mock whenever the proxy returns nothing (no key / provider down).
// Every hook returns { rows, live } so callers can label the data source.

import { useEffect, useState } from 'react';
import { fetchEconWeek, type EconEvent, fetchNews, fetchEarnings, fetchIpo, fetchDividends, fetchOhlc, fetchOptions, fetchMt5Accounts, fetchFundamentals, fetchFinancials, fetchScreen, fetchFed, fetchRegime, fetchCot, fetchFx, fetchWorld, fetchWorldBrief, fetchMt5Sources, fetchMt5Ohlc, fetchMt5Quote, type NewsItem, type Fundamental, type Financials, type FedPolicy, type MacroRegime, type CotRow, type FxRow, type WorldPulse, type WorldBrief, type Mt5Source } from '../services/api';
import { subscribeMt5 } from '../services/mt5stream';
import { NEWS_EVENT, type NewsPush } from '../live';

// Aggregated MT5 timeframes are folded from MN server-side, so an MN push is
// what signals them to refresh.
const MT5_BASE_TF: Record<string, string> = { '3MN': 'MN', '6MN': 'MN', '12MN': 'MN' };

export interface Live<T> { rows: T; live: boolean; }

// Force a component to re-render on every live price tick so anything it computes
// from live values (prices, %change, support/resistance, scores, valuations…) is
// recalculated in real time. Throttled to ~4/s so heavy pages stay smooth.
export function useLiveTick(throttleMs = 250): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let last = 0;
    const onTick = () => {
      const now = Date.now();
      if (now - last >= throttleMs) { last = now; setTick(x => x + 1); }
    };
    window.addEventListener('mm-live-tick', onTick);
    return () => window.removeEventListener('mm-live-tick', onTick);
  }, [throttleMs]);
  return tick;
}

/**
 * The global-situation band. Every source behind it is cached server-side on
 * its own clock (quakes 15 min, hazards 1 h, press 30 min, tape 5 min), so the
 * client just asks every few minutes and lets the server decide what is worth
 * re-fetching. Returns null until the first answer — the band renders nothing
 * rather than an empty frame.
 */
const WORLD_POLL_MS = Number(import.meta.env.VITE_WORLD_POLL_MS) || 300000;
export function useWorld(): WorldPulse | null {
  const [data, setData] = useState<WorldPulse | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => fetchWorld().then(d => { if (alive && d && typeof d.score === 'number') setData(d); }).catch(() => {});
    load();
    const h = setInterval(load, WORLD_POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(h); window.removeEventListener('focus', onFocus); };
  }, []);
  return data;
}

/**
 * The AI world brief. Generated server-side on a three-hour clock and shared by
 * every reader, so this only has to ask occasionally — and it asks once on
 * mount, since the first caller of the day is what triggers generation.
 * Returns 'loading' until the first answer, then the brief or { error }.
 */
export type BriefState = 'loading' | WorldBrief | { error: string };
export function useWorldBrief(): BriefState {
  const [state, setState] = useState<BriefState>('loading');
  useEffect(() => {
    let alive = true;
    const load = () => fetchWorldBrief().then(d => { if (alive) setState(d as BriefState); });
    load();
    // Re-ask on the same clock the server regenerates on; a mid-window ask is
    // free anyway (it is served from the server's cache).
    const h = setInterval(load, 30 * 60 * 1000);
    return () => { alive = false; clearInterval(h); };
  }, []);
  return state;
}

// Slow heartbeat that re-renders the caller, so relative timestamps
// ("12 นาทีที่แล้ว") keep counting on a page nobody is touching. Returns the
// current epoch ms in case the caller wants it.
export function useClock(intervalMs = 30000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const h = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(h);
  }, [intervalMs]);
  return now;
}

// Live headlines, pushed. The server watches the wire and pushes a `news` frame
// down the shared SSE the moment a headline breaks (see server/stream.mjs), so
// the feed updates in seconds without a reload. The REST poll below is the
// fallback for when the stream can't run (no key, proxy strips SSE) and the
// safety net after a sleep/reconnect — it is a backstop, not the mechanism.
//
// Whether a story is NEW is decided here, not by the server: this tab compares
// each batch against the URLs it has already displayed, so badges are right on
// the pushed path and the polled path alike, and a reconnect doesn't re-badge
// stories the reader already scrolled past.
const NEWS_POLL_MS = Number(import.meta.env.VITE_NEWS_POLL_MS) || 60000;
const NEWS_NEW_MS = Number(import.meta.env.VITE_NEWS_NEW_MS) || 120000; // how long a row stays badged

export interface LiveNews extends Live<any[]> {
  /** URLs that arrived while this tab was open, within the last NEWS_NEW_MS */
  fresh: Set<string>;
  /** epoch ms of the last batch applied (0 = still showing the fallback) */
  at: number;
}

const EMPTY_FRESH: Set<string> = new Set();

export function useNews(fallback: any[], limit = 12): LiveNews {
  const [state, setState] = useState<LiveNews>({ rows: fallback, live: false, fresh: EMPTY_FRESH, at: 0 });
  useEffect(() => {
    let alive = true;
    const known = new Set<string>();          // every URL this tab has shown
    const stamps = new Map<string, number>(); // URL → when it first appeared here

    const prune = () => {
      const now = Date.now();
      let cut = false;
      for (const [u, ts] of stamps) if (now - ts > NEWS_NEW_MS) { stamps.delete(u); cut = true; }
      return cut;
    };

    const apply = (rows: any[], at: number) => {
      if (!alive || !rows.length) return;
      prune();
      const first = known.size === 0;         // the first batch is the baseline
      for (const n of rows) {
        const u = n.url || n.t;
        if (!u || known.has(u)) continue;
        known.add(u);
        if (!first) stamps.set(u, Date.now()); // arrived while we were watching
      }
      setState({ rows: rows.slice(0, limit), live: true, fresh: new Set(stamps.keys()), at });
    };

    const load = () => fetchNews(limit).then((arr: NewsItem[]) => apply(arr, Date.now())).catch(() => {});
    load();

    // Pushed batches carry the full feed; the server also sends its own `fresh`
    // list, which we ignore in favour of this tab's own reckoning above.
    const onPush = (e: Event) => {
      const d = (e as CustomEvent<NewsPush>).detail;
      if (d && Array.isArray(d.items)) apply(d.items, d.at || Date.now());
    };
    window.addEventListener(NEWS_EVENT, onPush);

    const id = setInterval(load, NEWS_POLL_MS);
    // Let badges expire on their own so a row doesn't read NEW for an hour.
    const expire = setInterval(() => {
      if (stamps.size && prune() && alive) setState(s => ({ ...s, fresh: new Set(stamps.keys()) }));
    }, 15000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      alive = false;
      clearInterval(id); clearInterval(expire);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(NEWS_EVENT, onPush);
    };
  }, [limit]);
  return state;
}

// No sample data any more. When a window came back empty - or the request
// failed - this used to show the bundled mock rows instead: made-up IPOs
// ("StarGate Robotics", "NexaAI"), earnings with invented EPS and BEAT/MISS
// calls worked out from them, all on a page that never said it was not live.
// Now an empty window is empty, a failure is { failed: true }, and the
// `fallback` argument is ignored (kept so the call sites read the same).
export interface LiveCal extends Live<any[]> { failed: boolean; loading: boolean }
function useCalendar(
  fetcher: (from: string, to: string) => Promise<any[] | null>,
  from: string, to: string, _fallback?: any[],
): LiveCal {
  const [state, setState] = useState<LiveCal>({ rows: [], live: false, failed: false, loading: true });
  useEffect(() => {
    let alive = true;
    setState(s => ({ ...s, loading: true }));
    fetcher(from, to).then(arr => {
      if (!alive) return;
      setState(arr ? { rows: arr, live: true, failed: false, loading: false } : { rows: [], live: false, failed: true, loading: false });
    });
    return () => { alive = false; };
  }, [from, to]);
  return state;
}

/** This week's economic calendar, refreshed every half hour. */
export function useEconWeek(): { events: EconEvent[]; loaded: boolean; failed: boolean } {
  const [state, setState] = useState<{ events: EconEvent[]; loaded: boolean; failed: boolean }>({ events: [], loaded: false, failed: false });
  useEffect(() => {
    let alive = true;
    const load = () => fetchEconWeek().then(d => {
      if (!alive) return;
      if (d) setState({ events: Array.isArray(d.events) ? d.events : [], loaded: true, failed: false });
      else setState(s => ({ ...s, loaded: true, failed: true }));   // keep the last good week on screen
    });
    load();
    const h = setInterval(load, 30 * 60 * 1000);
    return () => { alive = false; clearInterval(h); };
  }, []);
  return state;
}

export const useEarnings = (from: string, to: string, fallback?: any[]) => useCalendar(fetchEarnings, from, to, fallback);
export const useIpo = (from: string, to: string, fallback?: any[]) => useCalendar(fetchIpo, from, to, fallback);
export const useDividends = (from: string, to: string, fallback?: any[]) => useCalendar(fetchDividends, from, to, fallback);

/**
 * Real historical OHLC for charts. Shows the simulated `fallback` immediately
 * (so the chart is never blank), then swaps in real Yahoo candles once loaded.
 * Falls back to simulation when no symbol maps / fetch fails / too few bars.
 */
/** Real options OI chain (Yahoo). Returns { data, loading }; data is null when unavailable. */
export function useOptions(symbol: string | null, date?: number | null): { data: any; loading: boolean } {
  const [state, setState] = useState<{ data: any; loading: boolean }>({ data: null, loading: !!symbol });
  useEffect(() => {
    let alive = true;
    if (!symbol) { setState({ data: null, loading: false }); return; }
    setState(s => ({ data: s.data, loading: true }));
    fetchOptions(symbol, date || undefined).then(d => { if (alive) setState({ data: d, loading: false }); });
    return () => { alive = false; };
  }, [symbol, date]);
  return state;
}

/**
 * Real fundamentals (price/mcap/P-E/EPS/div-yield) for a set of Yahoo symbols.
 * Returns a map keyed by Yahoo symbol plus a `live` flag (true once any real
 * row arrives). Empty map → callers keep their curated/mock dataset.
 */
export function useFundamentals(yahooSymbols: string[]): { data: Record<string, Fundamental>; live: boolean } {
  const key = yahooSymbols.join(',');
  const [state, setState] = useState<{ data: Record<string, Fundamental>; live: boolean }>({ data: {}, live: false });
  useEffect(() => {
    let alive = true;
    if (!key) { setState({ data: {}, live: false }); return; }
    fetchFundamentals(key.split(',')).then(d => {
      if (alive && d && Object.keys(d).length) setState({ data: d, live: true });
    });
    return () => { alive = false; };
  }, [key]);
  return state;
}

/**
 * Annual statements, P/BV, ROE and margins for one company. Cached six hours
 * server-side, so this fires on every symbol change without thought. Empty
 * object → the caller keeps whatever it had.
 */
export function useFinancials(yahooSymbol: string | null): { data: Financials; live: boolean } {
  const [state, setState] = useState<{ data: Financials; live: boolean }>({ data: {}, live: false });
  useEffect(() => {
    let alive = true;
    if (!yahooSymbol) { setState({ data: {}, live: false }); return; }
    setState({ data: {}, live: false });
    fetchFinancials(yahooSymbol).then(d => {
      if (alive && d && (d.annual?.length || d.keyStats)) setState({ data: d, live: true });
    }).catch(() => {});
    return () => { alive = false; };
  }, [yahooSymbol]);
  return state;
}

/**
 * Technical + latest-quarter screen for a set of Yahoo symbols, one batched
 * call. Cached six hours server-side; empty map → the caller keeps its own.
 */
export function useScreen(yahooSymbols: string[]): Record<string, any> {
  const key = yahooSymbols.join(',');
  const [data, setData] = useState<Record<string, any>>({});
  useEffect(() => {
    let alive = true;
    if (!key) { setData({}); return; }
    fetchScreen(key.split(',')).then(d => { if (alive && d) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [key]);
  return data;
}

/**
 * Fed target range, dot plot and meeting odds. Refreshed hourly — the target
 * moves eight times a year, the projection four, and the futures behind the
 * odds are cached fifteen minutes server-side.
 */
export function useFed(): FedPolicy {
  const [data, setData] = useState<FedPolicy>({});
  useEffect(() => {
    let alive = true;
    const load = () => fetchFed().then(d => { if (alive && d) setData(d); }).catch(() => {});
    load();
    const h = setInterval(load, 3600000);
    return () => { alive = false; clearInterval(h); };
  }, []);
  return data;
}

/** Macro regime — refreshed hourly; the underlying series are monthly. */
export function useRegime(): MacroRegime {
  const [data, setData] = useState<MacroRegime>({});
  useEffect(() => {
    let alive = true;
    const load = () => fetchRegime().then(d => { if (alive && d) setData(d); }).catch(() => {});
    load();
    const h = setInterval(load, 3600000);
    return () => { alive = false; clearInterval(h); };
  }, []);
  return data;
}

/** COT positioning. The report is weekly, so an hourly refresh is generous. */
export function useCot(): { asOf: string | null; rows: CotRow[] } {
  const [data, setData] = useState<{ asOf: string | null; rows: CotRow[] }>({ asOf: null, rows: [] });
  useEffect(() => {
    let alive = true;
    const load = () => fetchCot().then(d => { if (alive && d) setData(d); }).catch(() => {});
    load();
    const h = setInterval(load, 3600000);
    return () => { alive = false; clearInterval(h); };
  }, []);
  return data;
}

/** Currency valuation. Monthly data; refreshed hourly is more than enough. */
export function useFx(): { asOf: string | null; rows: FxRow[] } {
  const [data, setData] = useState<{ asOf: string | null; rows: FxRow[] }>({ asOf: null, rows: [] });
  useEffect(() => {
    let alive = true;
    const load = () => fetchFx().then(d => { if (alive && d) setData(d); }).catch(() => {});
    load();
    const h = setInterval(load, 3600000);
    return () => { alive = false; clearInterval(h); };
  }, []);
  return data;
}

/** MT5 account snapshots pushed by the user's MQL5 EA. Polls every `intervalMs`. */
export function useMt5(intervalMs = 15000): any[] {
  const [accts, setAccts] = useState<any[]>([]);
  useEffect(() => {
    let alive = true;
    const load = () => fetchMt5Accounts().then(a => { if (alive) setAccts(a); });
    load();
    const h = setInterval(load, intervalMs);
    return () => { alive = false; clearInterval(h); };
  }, [intervalMs]);
  return accts;
}

/** Brokers currently feeding the MT5 data pipe. Polls every 20s. */
export function useMt5Sources(intervalMs = 20000): Mt5Source[] {
  const [list, setList] = useState<Mt5Source[]>([]);
  useEffect(() => {
    let alive = true;
    const load = () => fetchMt5Sources().then(s => { if (alive) setList(s); }).catch(() => {});
    load();
    const h = setInterval(load, intervalMs);
    return () => { alive = false; clearInterval(h); };
  }, [intervalMs]);
  return list;
}

/**
 * OHLC candles from a specific MT5 broker. Updates instantly via SSE when the
 * EA pushes a new bar; `pollMs` is only a slow fallback if the stream drops.
 * Inactive (returns empty) when broker is null.
 */
export function useMt5Ohlc(broker: string | null, symbol: string, tf: string, pollMs = 30000): Live<any[]> {
  const [state, setState] = useState<Live<any[]>>({ rows: [], live: false });
  useEffect(() => {
    let alive = true;
    // Same reason as useMt5Quote: without this the previous symbol's candles
    // stay on the chart when the new one has no series here, or while its
    // fetch is in flight.
    setState({ rows: [], live: false });
    if (!broker) return;
    const load = () => fetchMt5Ohlc(broker, symbol, tf).then(d => {
      if (alive) setState({ rows: d.bars || [], live: !!d.live && (d.bars || []).length > 0 });
    }).catch(() => {});
    load();
    const SYM = symbol.toUpperCase();
    const wantTf = (MT5_BASE_TF[tf.toUpperCase()] || tf.toUpperCase());
    const unsub = subscribeMt5(e => {
      if (e.type === 'ohlc' && e.broker === broker && (e.symbol === SYM || e.base === SYM) && e.tf.toUpperCase() === wantTf) load();
    });
    const h = setInterval(load, pollMs);
    return () => { alive = false; unsub(); clearInterval(h); };
  }, [broker, symbol, tf, pollMs]);
  return state;
}

/**
 * Live quote from a specific MT5 broker. Updates instantly via SSE; `pollMs` is
 * only a slow fallback. null broker = inactive.
 */
export function useMt5Quote(broker: string | null, symbol: string, pollMs = 20000): { price: number; chg: number; ageMs: number } | null {
  const [q, setQ] = useState<{ price: number; chg: number; ageMs: number } | null>(null);
  useEffect(() => {
    let alive = true;
    // Clear FIRST. The old code kept the previous instrument's quote in state
    // across a symbol change and only replaced it if the new fetch returned
    // something - so switching from XAUUSD to a symbol the broker does not
    // carry left gold's price on screen under the new name, indefinitely.
    // analysis.tsx renders this as dispPrice, so that is a wrong price shown
    // as this instrument's, on a site people trade from.
    setQ(null);
    if (!broker) return;
    const SYM = symbol.toUpperCase();
    const load = () => fetchMt5Quote(broker, symbol).then(m => {
      const v = m[SYM];
      if (alive && v) setQ({ price: v.price, chg: v.chg, ageMs: v.ageMs });
    }).catch(() => {});
    load();
    const unsub = subscribeMt5(e => {
      if (e.type === 'quotes' && e.broker === broker) {
        const hit = e.quotes.find(x => x.sym === SYM);
        if (hit && alive) setQ({ price: hit.price, chg: hit.chg, ageMs: 0 });
      }
    });
    const h = setInterval(load, pollMs);
    return () => { alive = false; unsub(); clearInterval(h); };
  }, [broker, symbol, pollMs]);
  return q;
}

const OHLC_POLL_MS = Number(import.meta.env.VITE_OHLC_POLL_MS) || 60000;
export function useOhlc(symbol: string | null, params: { tf?: string; range?: string; interval?: string }, fallback: any[]): Live<any[]> {
  const key = params.tf || `${params.range}:${params.interval}`;
  const [state, setState] = useState<Live<any[]>>({ rows: fallback, live: false });
  useEffect(() => {
    let alive = true;
    setState({ rows: fallback, live: false }); // instant simulated view while loading
    if (!symbol) return;
    // Re-fetch on an interval so genuinely new bars (and the forming last bar's
    // OHLC) roll in without a reload; the live tick overlays the intra-bar price.
    const load = () => fetchOhlc(symbol, params).then(arr => {
      if (alive && arr.length >= 20) setState({ rows: arr, live: true });
    }).catch(() => {});
    load();
    const h = setInterval(load, OHLC_POLL_MS);
    return () => { alive = false; clearInterval(h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, key]);
  return state;
}
